import { usePlayerPreviewMutation, usePlayerQuery } from '../../../api/api'
import { useEffect, useRef, useState } from 'react'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import Tabs from 'components/Tabs/Tabs'
import clsx from 'clsx'
import IconMenu from '../../../components/IconMenu/IconMenu'
import Dropdown from '../../../components/Dropdown/Dropdown'
import { ReactComponent as ScreenSizeIcon } from 'assets/icons/responsive.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import LoadingBlocker from '../../../components/LoadingSpinner/LoadingBlocker'
import { ReactComponent as PrevIcon } from 'assets/icons/chev-prev.svg'
import ColorInput from '../../../components/ColorInput/ColorInput'
import { ReactComponent as PaintBucketIcon } from 'assets/icons/color-bucket.svg'
import PlaylistEditModalGeneralTab from './tabs/PlaylistEditModalGeneralTab'
import useDebounce from '../../../utils/hooks/useDebounce'
import PlaylistEditModalTrackTab from './tabs/PlaylistEditModalTrackTab'
import PlaylistEditModalWaveformTab from './tabs/PlaylistEditModalWaveformTab'
import PlaylistEditModalFolderTab from './tabs/PlaylistEditModalFolderTab'

export const defaultEmbeddedSettings = {
  trackArtSize: 'normal',
  showWaveform: 1,
  showPlayControls: 1,
  showTrackInformation: 1,
  showTrackProgressBar: 0,
  letterSpacing: 0,
  fgColor: null,
  bgColor: null,
  highlightColor: null,
  primaryColor: '#d63b5c',
  secondaryColor: '#787878',
  trackBackground: null,
  trackBackgroundHover: '#111114',
  trackPlayButtonColor: '#fff',
  currentlyPlayingText: {
    size: '16px',
    color: '#fff',
    bold: true,
    hidden: false,
    allCaps: false,
  },
  durationText: {
    size: '14px',
    color: '#fff',
    bold: false,
    hidden: false,
    allCaps: false,
  },
  trackTitleText: {
    size: '14px',
    color: '#fff',
    bold: true,
    hidden: false,
    allCaps: false,
  },
  trackArtistText: {
    size: '12px',
    color: '#cecece',
    bold: false,
    hidden: true,
    allCaps: false,
  },
  trackAlbumText: {
    size: '12px',
    color: '#cecece',
    bold: false,
    hidden: false,
    allCaps: false,
  },
  trackDurationText: {
    size: '14px',
    color: '#fff',
    bold: false,
    hidden: false,
    allCaps: false,
  },
  metadataLabelText: {
    size: '14px',
    color: '#cecece',
    bold: true,
    hidden: false,
    allCaps: false,
  },
  metadataValueText: {
    size: '16px',
    color: '#fff',
    bold: false,
    hidden: false,
    allCaps: false,
  },
  metadataArtSize: 'normal',
  folderTitleText: {
    size: '20px',
    color: '#fff',
    bold: true,
    hidden: false,
    allCaps: false,
  },
  folderDescriptionText: {
    size: '12px',
    color: '#dedede',
    bold: false,
    hidden: false,
    allCaps: false,
  },
  folderBorderColor: '#aaaaaa',
  folderChevronColor: '#ffffff',
  folderArtSize: 'normal',
}

const tabs = [
  {
    id: 'general',
    name: 'General',
  },
  {
    id: 'waveform',
    name: 'Waveform',
  },
  {
    id: 'track',
    name: 'Tracks',
  },
  {
    id: 'folder',
    name: 'Folders',
  },
]

const tabComponents = {
  general: PlaylistEditModalGeneralTab,
  track: PlaylistEditModalTrackTab,
  waveform: PlaylistEditModalWaveformTab,
  folder: PlaylistEditModalFolderTab,
}

const PlaylistEditModal = ({
  id,
  values: initialValues,
  data,
  onCloseClick,
  playlistSrc,
  onSubmit = (values, flags) => {},
  isLoading,
  mode,
}) => {
  const ref = useRef(null)
  const containerRef = useRef(null)
  const [values, setValues] = useState({
    ...defaultEmbeddedSettings,
    ...initialValues,
  })
  const [selectedTab, setSelectedTab] = useState('general')
  const [getPlayerPreview] = usePlayerPreviewMutation()
  const { previewBackgroundColor } = values || {}

  const [maxWidth, setMaxWidth] = useState('100%')
  const debouncedFetchPlayer = useDebounce(1000)

  const Tab = tabComponents[selectedTab]

  useEffect(() => {
    if (id || playlistSrc) {
      fetchPlayer(values)
    }
  }, [id])

  useEffect(() => {
    const mpOnReady = (evt) => {
      const container = evt.detail.container
      const buttons = [...container.querySelectorAll('button')]
      buttons.forEach((button) => button.classList.add('disable-hover'))
    }
    document.addEventListener('mpOnReady', mpOnReady)
    return () => document.removeEventListener('mpOnReady', mpOnReady)
  })

  async function fetchPlayer(playerValues) {
    const styleTags = [
      ...document.querySelectorAll('style[data-player-uuid="preview"]'),
    ]
    const div = document.createElement('div')
    const script = document.createElement('script')

    const preview = await getPlayerPreview({ data, styles: playerValues })
    div.id = 'preview'
    script.type = 'text/javascript'
    script.text = preview?.data?.script
    div.appendChild(script)
    ref.current.replaceChildren(div)
    styleTags.forEach((tag) => tag.remove())
  }

  function setValue(name, value) {
    const newValues = { ...values, [name]: value }
    setValues(newValues)
    debouncedFetchPlayer(() => {
      fetchPlayer(newValues)
    })
  }

  function onSaveClick() {
    onSubmit(values, {
      debounced: false,
    })
  }

  function onPublishClick() {
    onSubmit(values, {
      debounced: false,
      publish: true,
    })
  }

  function handleBackgroundColorChange(value) {
    setValues({ ...values, previewBackgroundColor: value })
  }

  return (
    <div className="flex flex-col bg-gray-light h-full" ref={containerRef}>
      <div className="px-6 pt-4 pb-6 bg-gray-light">
        <button
          onClick={onCloseClick}
          className="flex items-center justify-between mb-2"
        >
          <PrevIcon fill="#ccc" />{' '}
          <span className="block ml-2 text-sm">Back</span>
        </button>
        <h2 className="">Embedded Player</h2>
      </div>
      <div className="flex flex-1">
        <div className="w-[650px] flex flex-col">
          <div className="bg-gray-dark h-[40px] px-6 items-center flex">
            <IconMenu
              icon={<ScreenSizeIcon fill="#ccc" width="34" height="34" />}
              tooltip="Preview Size"
            >
              <button onClick={() => setMaxWidth('100%')}>Full</button>
              <button onClick={() => setMaxWidth('400px')}>
                Compact / Mobile
              </button>
            </IconMenu>{' '}
            <ColorInput
              tooltip="Preview Background Color"
              hideInput
              hideColorPreview
              hideClear
              containerClass="ml-4"
              value={previewBackgroundColor}
              containerRef={containerRef}
              icon={<PaintBucketIcon fill="#ccc" />}
              onChange={handleBackgroundColorChange}
            />
          </div>
          <div
            className={clsx(
              'relative flex justify-center p-4 flex-1 basis-0 overflow-y-auto',
            )}
            style={{ backgroundColor: previewBackgroundColor || '#202124' }}
          >
            <div ref={ref} style={{ maxWidth, flex: 1 }}></div>
          </div>
        </div>
        <div className="flex-1 bg-gray-lighter flex flex-col">
          <div>
            <Tabs
              tabs={tabs}
              selectedTab={selectedTab}
              onClick={(tab) => setSelectedTab(tab.id)}
            />
          </div>
          <div className="flex-1 basis-0 overflow-y-auto">
            <Tab
              values={values}
              setValue={setValue}
              onSaveClick={onSaveClick}
              onSavePublishClick={onPublishClick}
              publishLabel={
                mode === 'portfolio' ? 'Save and Close' : 'Save and Publish'
              }
            />
          </div>
        </div>
      </div>

      <button className="p-4 absolute top-0 right-0" onClick={onCloseClick}>
        <CloseIcon fill="#CCC" height="26" />
      </button>

      {isLoading ? <LoadingBlocker /> : null}
    </div>
  )
}

export default PlaylistEditModal
